import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createAPIKey } from 'api/apiKeys';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import * as Input from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { apiQueryKeys } from 'queries/api-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface NewAPIKeyFormValues {
    name?: string;
}

const NewAPIKeyDrawer = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { goBack } = useDialogRoute();
    const [plainTextKey, setPlainTextKey] = useState('');
    const methods = Form.useGivebutterForm<NewAPIKeyFormValues>({
        defaultValues: {
            name: '',
        },
    });
    const { handleSubmit, control, path, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: NewAPIKeyFormValues) => createAPIKey(accountId, { name: values.name }),
        onSuccess: data => {
            setPlainTextKey(data.plainTextApiKey);
            toast.success('Generated API key successfully');
            queryClient.invalidateQueries({ queryKey: apiQueryKeys.apiKeys(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            resetFormButton();
            toast.error(e.response?.data.message ?? 'Failed to generate API key');
        },
    });

    return (
        <>
            <Form.GivebutterFormProvider {...methods}>
                <form
                    className={'contents'}
                    onSubmit={handleSubmit(submit as SubmitHandler<NewAPIKeyFormValues>)}
                >
                    <Drawer.Header>
                        <Drawer.Title>New API key</Drawer.Title>
                    </Drawer.Header>
                    <Drawer.Body>
                        <Form.Container>
                            <Form.Label name={path('name')}>API key name</Form.Label>
                            <Form.TextInputRoot control={control} name="name">
                                <Form.TextInputForm placeholder="E.g. Zapier" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>
                    </Drawer.Body>
                    <Drawer.Footer>
                        <div
                            className={
                                'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                            }
                        >
                            <Drawer.Cancel asChild={true}>
                                <Button variant="transparent">Cancel</Button>
                            </Drawer.Cancel>

                            <Form.SubmitButton>Create</Form.SubmitButton>
                        </div>
                    </Drawer.Footer>
                </form>
            </Form.GivebutterFormProvider>
            <AlertDialog.Root type="warning" open={plainTextKey !== ''}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content>
                        <AlertDialog.Header>
                            <AlertDialog.Icon />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <AlertDialog.Title>Copy your API key</AlertDialog.Title>
                            <AlertDialog.Description>
                                <div className="flex flex-col gap-4">
                                    <p>
                                        Make sure to copy your API key now. You won't be able to see
                                        it again!
                                    </p>
                                    <Input.Root>
                                        <Input.Form readOnly={true} value={plainTextKey} />
                                        <Input.CopyButton text={plainTextKey} inPortal={true} />
                                    </Input.Root>
                                </div>
                            </AlertDialog.Description>
                        </AlertDialog.Body>
                        <AlertDialog.Footer>
                            <AlertDialog.Action
                                onClick={() => {
                                    setPlainTextKey('');
                                    goBack();
                                }}
                            >
                                Done
                            </AlertDialog.Action>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </>
    );
};

export { NewAPIKeyDrawer };
